import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { UWidgetSpec, UWidgetAction, UWidgetEvent } from '../core/types.js';
import { validate, specErrorMessage, type ValidationIssue } from '../core/schema.js';
import { normalize } from '../core/normalize.js';
import { infer } from '../core/infer.js';
import { suggestWidget } from '../core/suggest.js';
import { widgetEntry, type WidgetEntry } from '../core/entries.js';
import { resolveLocale, onLocaleChange, getLocaleRevision, getLocaleStrings, formatTemplate, splitTemplate } from '../core/locale.js';
import { themeStyles } from '../styles/tokens.js';
import './uw-metric.js';
import './uw-gauge.js';
import './uw-table.js';
import './uw-form.js';
import './uw-compose.js';
import './uw-content.js';
import './uw-kv.js';
import './uw-code.js';
import './uw-citation.js';
import './uw-status.js';
import './uw-steps.js';
import './uw-rating.js';
import './uw-video.js';
import './uw-gallery.js';

/** Widget types already checked for a missing entry point — at most one console line per type per page. */
const checkedMissingEntries = new Set<string>();

/**
 * How long an entry point may take to register before its absence is reported. A host that imports
 * the entry lazily (`import('@iyulab/u-widgets/charts')`, so it loads beside the app rather than in
 * front of it) renders its first widgets before the entry arrives; that is correct, not a mistake to
 * warn about. Only an entry still missing after this long is one the host never imported.
 */
const MISSING_ENTRY_GRACE_MS = 5000;

/**
 * <u-widget> — Entry point router element.
 * Reads spec.widget and delegates to the appropriate sub-component.
 *
 * Supports theme attribute: theme="dark" | theme="light" | (auto via color-scheme inheritance)
 */
@customElement('u-widget')
export class UWidget extends LitElement {
  static styles = [
    themeStyles,
    css`
      




      :host {
        display: flex;
        flex-direction: column;
        container: u-widget / inline-size;
      }

      



      :host > *,
      [part="widget-container"] > * {
        flex: 1 1 auto;
        min-height: 0;
      }

      .widget-title,
      .global-actions {
        flex: none;
      }

      .error-card {
        padding: 10px 14px;
        border-radius: 6px;
        border: 1px solid var(--u-widget-negative, #dc2626);
        background: color-mix(in srgb, var(--u-widget-negative, #dc2626) 8%, var(--u-widget-bg, #fff));
        font-size: var(--u-widget-font-size-label, 0.8125rem);
      }

      .error-header { font-weight: 600; color: var(--u-widget-negative, #dc2626); margin-bottom: 4px; }
      .error-list { margin: 0; padding-left: 20px; color: var(--u-widget-text-secondary, #5b6777); }

      .fallback-card {
        padding: 10px 14px;
        border-radius: 6px;
        border: 1px dashed var(--u-widget-border, #e2e8f0);
        font-size: var(--u-widget-font-size-label, 0.8125rem);
      }
      .fallback-label { font-size: var(--u-widget-font-size-overline, 0.6875rem); font-weight: 600; color: var(--u-widget-text-secondary, #5b6777); margin-bottom: 6px; }
      .fallback-hint { font-size: var(--u-widget-font-size-caption, 0.75rem); color: var(--u-widget-primary, #4f46e5); margin-bottom: 6px; }
      .fallback-card pre { margin: 0; white-space: pre-wrap; font-size: var(--u-widget-font-size-caption, 0.75rem); color: var(--u-widget-text-secondary, #5b6777); max-height: 200px; overflow-y: auto; }

      
      .card-container {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        padding: 16px 20px;
        border-radius: var(--u-widget-radius, 6px);
        border: 1px solid var(--u-widget-border, #e2e8f0);
        background: var(--u-widget-bg, #fff);
        box-shadow: var(--u-widget-shadow);
        transition: box-shadow var(--u-widget-duration-normal, 200ms);
      }

      .card-container > * {
        flex: 1 1 auto;
        min-height: 0;
      }

      
      .card-container > .widget-title {
        flex: none;
      }

      
      [part="widget-container"] {
        display: contents;
      }

      [part="action-btn"] {
        padding: 8px 20px;
        border-radius: 6px;
        font-size: var(--u-widget-font-size, 0.875rem);
        font-weight: 500;
        cursor: pointer;
        border: 1px solid var(--u-widget-border, #e2e8f0);
        background: var(--u-widget-bg, #fff);
        color: var(--u-widget-text, #1a1a2e);
        transition: all var(--u-widget-duration-fast, 150ms);
        font-family: inherit;
      }

      [part="action-btn"]:hover {
        background: var(--u-widget-surface, #f1f5f9);
      }

      [part="action-btn"][data-style='primary'] {
        background: var(--u-widget-primary, #4f46e5);
        color: var(--u-widget-on-primary, #fff);
        border-color: transparent;
      }

      [part="action-btn"][data-style='primary']:hover {
        opacity: 0.9;
      }

      [part="action-btn"][data-style='danger'] {
        background: var(--u-widget-negative, #dc2626);
        color: var(--u-widget-on-negative, #fff);
        border-color: transparent;
      }

      [part="action-btn"]:disabled {
        opacity: 0.5;
        cursor: default;
      }

      
      .widget-title {
        font-size: var(--u-widget-font-size, 0.875rem);
        font-weight: 600;
        color: var(--u-widget-text, #1a1a2e);
        margin: 0 0 8px 0;
      }

      
      .global-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        padding-top: 10px;
        flex-wrap: wrap;
      }

      
      .actions-widget {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }

      .actions-widget[data-layout='column'] {
        flex-direction: column;
      }

      
      .divider {
        display: flex;
        align-items: center;
        gap: 12px;
        color: var(--u-widget-text-secondary, #5b6777);
        font-size: var(--u-widget-font-size-caption, 0.75rem);
      }

      .divider::before,
      .divider::after {
        content: '';
        flex: 1;
        border-top: 1px solid var(--u-widget-border, #e2e8f0);
      }

      .divider:empty::after { display: none; }
      .divider:empty { border-top: 1px solid var(--u-widget-border, #e2e8f0); }
      .divider:empty::before { display: none; }

      .divider-spacing-small { margin: 4px 0; }
      .divider-spacing-default { margin: 12px 0; }
      .divider-spacing-large { margin: 24px 0; }

      
      .header-widget {
        font-weight: 600;
        color: var(--u-widget-text, #1a1a2e);
        margin: 0;
      }

      .header-widget[data-level='1'] { font-size: 1.5rem; }
      .header-widget[data-level='2'] { font-size: 1.25rem; }
      .header-widget[data-level='3'] { font-size: 1.1rem; }

      
      @container u-widget (max-width: 20rem) {
        .global-actions {
          flex-direction: column;
        }

        .global-actions [part="action-btn"] {
          width: 100%;
          text-align: center;
        }

        .actions-widget:not([data-layout='column']) {
          flex-direction: column;
        }

        .actions-widget [part="action-btn"] {
          width: 100%;
          text-align: center;
        }

        .card-container {
          padding: 12px 14px;
        }

        [part="action-btn"] {
          padding: 8px 14px;
          font-size: var(--u-widget-font-size-label, 0.8125rem);
        }

        .header-widget[data-level='1'] { font-size: 1.25rem; }
        .header-widget[data-level='2'] { font-size: 1.1rem; }
        .header-widget[data-level='3'] { font-size: 1rem; }
      }
    `,
  ];

  @property({
    type: Object,
    converter: {
      fromAttribute(value: string | null) {
        if (!value) return null;
        try {
          return JSON.parse(value);
        } catch (e) {
          console.warn('[@iyulab/u-widgets] u-widget: invalid JSON in the spec attribute:', (e as Error).message);
          return null;
        }
      },
    },
  })
  spec: UWidgetSpec | null = null;

  /** Theme override: "dark" | "light" | null (auto via color-scheme inheritance). */
  @property({ type: String, reflect: true })
  theme: string | null = null;

  /** Locale tag (e.g. 'ko', 'en-US'). Propagated to sub-components via spec.options.locale. */
  @property({ type: String, reflect: true })
  locale: string | null = null;

  /** Entry-point elements this instance is waiting on, so it re-renders once each is registered. */
  private _awaitedElements = new Set<string>();

  /** This widget's chrome strings — its own `locale`, else the default locale. */
  private get _strings() {
    return getLocaleStrings(resolveLocale(this.locale));
  }

  /** Locale subscription while connected, and the revision seen when detached (to catch up on re-attach). */
  private _unsubscribeLocale?: () => void;
  private _detachedLocaleRevision?: number;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('u-widget-internal', this._handleInternalEvent as EventListener);
    // A runtime language switch re-renders what is already on screen; a switch while detached is caught up now.
    this._unsubscribeLocale = onLocaleChange(() => this.requestUpdate());
    if (this._detachedLocaleRevision !== undefined && this._detachedLocaleRevision !== getLocaleRevision()) this.requestUpdate();
    this._detachedLocaleRevision = undefined;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('u-widget-internal', this._handleInternalEvent as EventListener);
    this._unsubscribeLocale?.();
    this._unsubscribeLocale = undefined;
    this._detachedLocaleRevision = getLocaleRevision();
  }

  private _handleInternalEvent = (e: CustomEvent<UWidgetEvent>) => {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('u-widget-event', {
        detail: e.detail,
        bubbles: true,
        composed: true,
      }),
    );
  };

  render() {
    if (!this.spec) {
      return html`<slot></slot>`;
    }

    const result = validate(this.spec);
    if (!result.valid) {
      return this.renderError(result.issues);
    }

    const spec = normalize(this.spec);
    return this.renderWidget(spec);
  }

  private renderWidget(spec: UWidgetSpec) {
    const widget = spec.widget;

    // Auto-infer mapping if missing
    const mapping = spec.mapping ?? infer(widget, spec.data);
    let resolved = mapping ? { ...spec, mapping } : spec;

    // Propagate locale attribute to spec if not already set
    const effectiveLocale = resolveLocale(this.locale);
    if (effectiveLocale && !resolved.options?.locale) {
      resolved = {
        ...resolved,
        options: { ...resolved.options, locale: effectiveLocale },
      };
    }

    const t = this.theme;

    // Determine if actions should be rendered at router level
    // form/confirm handle their own actions; standalone "actions" widget also self-handles
    const selfHandlesActions = widget === 'form' || widget === 'confirm' || widget === 'actions';
    const globalActions = !selfHandlesActions ? (resolved.actions ?? []) : [];

    let widgetHtml;
    switch (widget) {
      case 'metric':
      case 'stat-group':
        widgetHtml = html`<uw-metric .spec=${resolved} theme=${t ?? nothing} nested></uw-metric>`;
        break;
      case 'gauge':
      case 'progress':
        widgetHtml = html`<uw-gauge .spec=${resolved} theme=${t ?? nothing} nested></uw-gauge>`;
        break;
      case 'table':
      case 'list':
        widgetHtml = html`<uw-table .spec=${resolved} theme=${t ?? nothing} nested></uw-table>`;
        break;
      case 'form':
      case 'confirm':
        widgetHtml = html`<uw-form .spec=${resolved} theme=${t ?? nothing} nested></uw-form>`;
        break;
      case 'compose':
        widgetHtml = html`<uw-compose .spec=${resolved} theme=${t ?? nothing} nested></uw-compose>`;
        break;
      case 'markdown':
      case 'image':
      case 'callout':
        widgetHtml = html`<uw-content .spec=${resolved} theme=${t ?? nothing} nested></uw-content>`;
        break;
      case 'kv':
        widgetHtml = html`<uw-kv .spec=${resolved} theme=${t ?? nothing} nested></uw-kv>`;
        break;
      case 'code':
        widgetHtml = html`<uw-code .spec=${resolved} theme=${t ?? nothing} nested></uw-code>`;
        break;
      case 'citation':
        widgetHtml = html`<uw-citation .spec=${resolved} theme=${t ?? nothing} nested></uw-citation>`;
        break;
      case 'status':
        widgetHtml = html`<uw-status .spec=${resolved} theme=${t ?? nothing} nested></uw-status>`;
        break;
      case 'steps':
        widgetHtml = html`<uw-steps .spec=${resolved} theme=${t ?? nothing} nested></uw-steps>`;
        break;
      case 'rating':
        widgetHtml = html`<uw-rating .spec=${resolved} theme=${t ?? nothing} nested></uw-rating>`;
        break;
      case 'video':
        widgetHtml = html`<uw-video .spec=${resolved} theme=${t ?? nothing} nested></uw-video>`;
        break;
      case 'gallery':
        widgetHtml = html`<uw-gallery .spec=${resolved} theme=${t ?? nothing} nested></uw-gallery>`;
        break;
      case 'actions':
        widgetHtml = this.renderActionsWidget(resolved);
        break;
      case 'divider':
        widgetHtml = this.renderDivider(resolved);
        break;
      case 'header':
        widgetHtml = this.renderHeader(resolved);
        break;
      default:
        // chart.* types are handled by uw-chart if loaded (separate entry point)
        if (widget.startsWith('chart.') && customElements.get('uw-chart')) {
          widgetHtml = html`<uw-chart .spec=${resolved} theme=${t ?? nothing} nested></uw-chart>`;
          break;
        }
        // math is handled by uw-math if loaded (separate entry point)
        if (widget === 'math' && customElements.get('uw-math')) {
          widgetHtml = html`<uw-math .spec=${resolved} theme=${t ?? nothing} nested></uw-math>`;
          break;
        }
        {
          const needed = widgetEntry(widget);
          if (needed) return this.renderMissingEntry(resolved, needed);
        }
        return this.renderFallback(resolved);
    }

    // Render title above widget (skip for types that consume title themselves)
    const titleExcluded = widget === 'header' || widget === 'actions' || widget === 'divider' || widget === 'compose';
    const titleHtml = !titleExcluded && resolved.title
      ? html`<div class="widget-title" part="title">${resolved.title}</div>`
      : nothing;

    // Wrap in card container if options.card is set
    const isCard = Boolean((resolved.options as Record<string, unknown>)?.card);
    if (isCard) {
      widgetHtml = html`<div class="card-container" part="card">${titleHtml}${widgetHtml}</div>`;
    } else if (titleHtml !== nothing) {
      widgetHtml = html`${titleHtml}${widgetHtml}`;
    }

    // Append global action bar if actions are present
    if (globalActions.length > 0) {
      const actionButtons = globalActions.map((a) => html`
        <button
          data-style=${a.style ?? 'default'}
          ?disabled=${a.disabled}
          @click=${() => this._dispatchAction(resolved, a)}
          part="action-btn"
        >${a.label}</button>
      `);
      return html`<div part="widget-container">${widgetHtml}<div class="global-actions" part="actions">${actionButtons}</div></div>`;
    }

    return widgetHtml;
  }

  /** Render standalone "actions" widget — a group of action buttons. */
  private renderActionsWidget(spec: UWidgetSpec) {
    const actions = spec.actions ?? [];
    if (actions.length === 0) return nothing;

    const options = (spec.options ?? {}) as Record<string, unknown>;
    const layout = String(options.layout ?? 'wrap');

    return html`
      <div class="actions-widget" data-layout=${layout} part="actions" role="group" aria-label=${spec.title ?? this._strings.actions}>
        ${actions.map((a) => html`
          <button
            data-style=${a.style ?? 'default'}
            ?disabled=${a.disabled}
            @click=${() => this._dispatchAction(spec, a)}
            part="action-btn"
          >${a.label}</button>
        `)}
      </div>
    `;
  }

  /** Render inline divider widget. */
  private renderDivider(spec: UWidgetSpec) {
    const options = (spec.options ?? {}) as Record<string, unknown>;
    const label = options.label as string | undefined;
    const spacing = String(options.spacing ?? 'default');

    return html`
      <div class="divider divider-spacing-${spacing}" part="divider" role="separator">
        ${label ?? nothing}
      </div>
    `;
  }

  /** Render inline header widget. */
  private renderHeader(spec: UWidgetSpec) {
    const data = spec.data as Record<string, unknown> | undefined;
    const text = String(data?.text ?? spec.title ?? '');
    const level = String(data?.level ?? '2');

    if (!text) return nothing;

    return html`
      <div class="header-widget" data-level=${level} part="header" role="heading" aria-level=${level}>
        ${text}
      </div>
    `;
  }

  /** Dispatch an action event from the global action bar or actions widget. */
  private _dispatchAction(spec: UWidgetSpec, action: UWidgetAction) {
    if (action.action === 'navigate' && action.url) {
      window.open(action.url, '_blank', 'noopener');
      return;
    }

    this.dispatchEvent(
      new CustomEvent('u-widget-event', {
        detail: {
          type: 'action',
          widget: spec.widget,
          id: spec.id,
          action: action.action,
          data: action.url ? { url: action.url } : undefined,
        } satisfies UWidgetEvent,
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * A known widget whose element lives in an entry point that has not been imported. Says which
   * entry is missing — in the card, and once per type in the console if it is still missing after a
   * short grace (a lazily imported entry is on its way, not forgotten) — instead of a generic fallback
   * whose label (the spec title) reads like a rendered widget. Re-renders when the element is
   * registered later, so a lazily imported entry takes over without the host doing anything.
   */
  private renderMissingEntry(spec: UWidgetSpec, needed: WidgetEntry) {
    if (!checkedMissingEntries.has(spec.widget)) {
      checkedMissingEntries.add(spec.widget);
      const widget = spec.widget;
      setTimeout(() => {
        if (!customElements.get(needed.element)) console.warn(`[@iyulab/u-widgets] u-widget: "${widget}" needs import '${needed.entry}'`);
      }, MISSING_ENTRY_GRACE_MS);
    }
    if (!this._awaitedElements.has(needed.element)) {
      this._awaitedElements.add(needed.element);
      void customElements.whenDefined(needed.element).then(() => this.requestUpdate());
    }
    const [addBefore, addAfter] = splitTemplate(this._strings.addImportHint, 'import');
    const importLine = `import '${needed.entry}'`; // code, not prose — it stays as written in every locale
    return html`
      <div class="fallback-card" part="fallback" data-missing-entry=${needed.entry}>
        <div class="fallback-label">${formatTemplate(this._strings.moduleNotLoaded, { widget: spec.widget })}</div>
        <div class="fallback-hint">${addBefore}<code>${importLine}</code>${addAfter}</div>
        <pre part="json"><code>${JSON.stringify(spec, null, 2)}</code></pre>
      </div>
    `;
  }

  private renderFallback(spec: UWidgetSpec) {
    const suggestion = suggestWidget(spec.widget);
    const [meanBefore, meanAfter] = splitTemplate(this._strings.didYouMean, 'suggestion');
    return html`
      <div class="fallback-card" part="fallback">
        <div class="fallback-label">${spec.title ?? formatTemplate(this._strings.unknownWidget, { widget: spec.widget })}</div>
        ${suggestion ? html`<div class="fallback-hint">${meanBefore}<strong>${suggestion}</strong>${meanAfter}</div>` : ''}
        <pre part="json"><code>${JSON.stringify(spec, null, 2)}</code></pre>
      </div>
    `;
  }

  private renderError(issues: ValidationIssue[]) {
    return html`
      <div class="error-card" part="error">
        <div class="error-header">
          <span aria-hidden="true">&#x26A0;</span>
          <span>${this._strings.invalidSpec}</span>
        </div>
        <ul class="error-list">
          ${issues.map((issue) => html`<li>${specErrorMessage(issue, this._strings)}</li>`)}
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'u-widget': UWidget;
  }
  // Bubbles and is composed, so a listener on `document` or any ancestor sees it typed too.
  interface GlobalEventHandlersEventMap {
    'u-widget-event': CustomEvent<UWidgetEvent>;
  }
}
